import { useState, useCallback } from "react";
import { message } from "antd";
import * as api from "../api";
import type { RecordTaskItem, ListRecordTasksParams } from "../api";

/**
 * 历史录制任务相关 hook
 */
export function useHistoryRecordTasks(selectedEnv: string | undefined, isRecording: boolean, setIsRecording: (v: boolean) => void, setCurrentTaskId: (v: string | null) => void) {
  const [historyModalVisible, setHistoryModalVisible] = useState<boolean>(false);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyData, setHistoryData] = useState<RecordTaskItem[]>([]);
  const [historyPagination, setHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取历史录制任务列表
  const fetchHistoryTasks = useCallback(async (pageNum = 1, pageSize = 10) => {
    setHistoryLoading(true);
    try {
      const params: ListRecordTasksParams = {
        page_number: pageNum,
        page_size: pageSize,
      };
      const res = await api.listRecordTasks(params);
      setHistoryData(res.data.items || []);
      setHistoryPagination({
        current: Math.max(1, res.data.page_num),
        pageSize: res.data.page_size,
        total: res.data.total_count,
      });
    } catch (error) {
      message.error("获取历史录制任务失败");
      setHistoryData([]);
      setHistoryPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // 打开历史记录弹窗并加载第一页数据
  const handleShowHistoryModal = async () => {
    await fetchHistoryTasks(1, historyPagination.pageSize);
    setHistoryModalVisible(true);
  };

  // 关闭历史记录弹窗
  const handleCloseHistoryModal = () => {
    setHistoryModalVisible(false);
    setHistoryData([]);
    setHistoryPagination({ current: 1, pageSize: 10, total: 0 });
  };

  // 处理历史记录弹窗表格分页变化
  const handleHistoryTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchHistoryTasks(newPagination.current, newPagination.pageSize);
  };

  // 处理停止单个历史任务
  const handleStopHistoryTask = async (taskId: string) => {
    const task = historyData.find((t) => t.task_id === taskId);
    const business = (task as any)?.business;
    const cluster_env = (task as any)?.cluster_env;
    message.loading({
      content: `正在停止任务 ${taskId}...`,
      key: `stop-${taskId}`,
    });
    try {
      await api.pauseRecordTask({ task_id: taskId, business, cluster_env });
      message.success({
        content: `任务 ${taskId} 已停止`,
        key: `stop-${taskId}`,
        duration: 2,
      });
      fetchHistoryTasks(historyPagination.current, historyPagination.pageSize);
      const stoppedTask = historyData.find((task) => task.task_id === taskId);
      if (
        stoppedTask &&
        stoppedTask.cluster_env === selectedEnv &&
        isRecording
      ) {
        setIsRecording(false);
        setCurrentTaskId(null);
      }
    } catch (error) {
      message.error({
        content: `停止任务 ${taskId} 失败: ${
          error instanceof Error ? error.message : "未知错误"
        }`,
        key: `stop-${taskId}`,
        duration: 3,
      });
    }
  };

  return {
    historyModalVisible,
    setHistoryModalVisible,
    historyLoading,
    historyData,
    historyPagination,
    fetchHistoryTasks,
    handleShowHistoryModal,
    handleCloseHistoryModal,
    handleHistoryTableChange,
    handleStopHistoryTask,
  };
} 